import AddRoundedIcon from '@mui/icons-material/AddRounded';
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  List,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ThirdPartEnum } from 'Interfaces/ICompaniesConfig';
import { IEmployeeViewModel } from 'Interfaces/IEmployeeConfig';
import {
  EnumObjectType,
  EnumProductVariant,
} from 'Interfaces/IGlobalTypeConfig';
import { IProductViewModel } from 'Interfaces/IProductConfig';
import { SingleEmployeeDrawerProductView } from 'components/ui/SingleEmployeeDrawerProductView';
import userAtom from 'data/atoms/userAtom';
import useWindowsDimension from 'functions/useWindowsDimension';
import { rq, rqPost } from 'http/apiRoutes';
import { useGet, usePost } from 'http/useApi';
import { t } from 'i18next';
import { useAtom } from 'jotai';
import { useEffect, useReducer, useState } from 'react';
import { SingleInvestmentProductView } from '../SingleInvestmentProductView';
import { SingleProductView } from '../SingleProductView';

// Types
interface State {
  productCatalogueList: IProductViewModel[];
  productInvestmentList: IProductViewModel[];
  productAgreementList: IProductViewModel[];
  productConsumableList: IProductViewModel[];
}

export type Action =
  | { type: 'SET_PRODUCT_LIST'; payload: IProductViewModel[] }
  | { type: 'ADD_INVESTMENT_PRODUCT'; payload: IProductViewModel }
  | { type: 'ADD_AGREEMENT_PRODUCT'; payload: IProductViewModel }
  | { type: 'ADD_CONSUMABLE_PRODUCT'; payload: IProductViewModel }
  | {
      type: 'REMOVE_INVESTMENT_PRODUCT';
      payload: { product: IProductViewModel; index: number };
    }
  | {
      type: 'REMOVE_AGREEMENT_PRODUCT';
      payload: { product: IProductViewModel; index: number };
    }
  | {
      type: 'REMOVE_CONSUMABLE_PRODUCT';
      payload: { product: IProductViewModel; index: number };
    }
  | { type: 'CLEAR_PRODUCTS'; payload?: IProductViewModel[] };

// Reducer and Initial State
const initialState: State = {
  productCatalogueList: [],
  productInvestmentList: [],
  productAgreementList: [],
  productConsumableList: [],
};

const productReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_PRODUCT_LIST':
      return { ...state, productCatalogueList: action.payload };
    case 'ADD_INVESTMENT_PRODUCT':
      return {
        ...state,
        productInvestmentList: [...state.productInvestmentList, action.payload],
      };
    case 'ADD_AGREEMENT_PRODUCT':
      return {
        ...state,
        productAgreementList: [...state.productAgreementList, action.payload],
      };
    case 'ADD_CONSUMABLE_PRODUCT':
      return {
        ...state,
        productInvestmentList: [...state.productInvestmentList, action.payload],
      };
    case 'REMOVE_INVESTMENT_PRODUCT': {
      const indexToRemove = action.payload.index;

      // Hvis produktet ikke finnes, returner uendret state
      if (indexToRemove === -1) {
        return state;
      }

      // Lag en kopi av arrayet og fjern én forekomst
      const updatedProducts = [...state.productInvestmentList];
      updatedProducts.splice(indexToRemove, 1); // Fjern kun én forekomst

      return {
        ...state,
        productInvestmentList: updatedProducts,
      };
    }
    case 'REMOVE_AGREEMENT_PRODUCT': {
      const indexToRemove = action.payload.index;

      // Hvis produktet ikke finnes, returner uendret state
      if (indexToRemove === -1) {
        return state;
      }

      // Lag en kopi av arrayet og fjern én forekomst
      const updatedProducts = [...state.productAgreementList];
      updatedProducts.splice(indexToRemove, 1); // Fjern kun én forekomst

      return {
        ...state,
        productAgreementList: updatedProducts,
      };
    }
    case 'REMOVE_CONSUMABLE_PRODUCT': {
      const indexToRemove = action.payload.index;

      // Hvis produktet ikke finnes, returner uendret state
      if (indexToRemove === -1) {
        return state;
      }

      // Lag en kopi av arrayet og fjern én forekomst
      const updatedProducts = [...state.productInvestmentList];
      updatedProducts.splice(indexToRemove, 1); // Fjern kun én forekomst

      return {
        ...state,
        productInvestmentList: updatedProducts,
      };
    }
    case 'CLEAR_PRODUCTS':
      return {
        ...state,
        productInvestmentList: [],
        productAgreementList: [],
        productConsumableList: [],
      };

    default:
      return state;
  }
};

type Props = {
  ticketId: string;
  ticketProductVariant: EnumProductVariant;
  objectDrawerList?: any[];
  companyId?: string;
  owner?: IEmployeeViewModel;
  vendors?: any[];
};

export const ProductDrawer = ({
  ticketId,
  ticketProductVariant,
  objectDrawerList,
  companyId,
  owner,
  vendors,
}: Props) => {
  const [state, dispatch] = useReducer(productReducer, initialState);
  const [user] = useAtom(userAtom);
  const { height } = useWindowsDimension();
  const [isOnboarding] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');

  const [companyFilter, setCompanyFilter] = useState<ThirdPartEnum>(
    ThirdPartEnum.None
  );
  const [page, setPage] = useState<number>(1);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [objectType] = useState<EnumObjectType>(EnumObjectType.Product);

  // API Call to fetch products
  let isGet = drawerOpen ? true : false;
  const { data: getProducts } = useGet(
    rq.getProductByType(ticketProductVariant, page, itemsPerPage, isGet),
    
  );

  const postSaleProducts = usePost(
    rqPost.productSaleList(ticketId, user.id || '', isGet),
    
  );

  useEffect(() => {
    if (getProducts) {
      // Dispatch data from API to state
      dispatch({
        type: 'SET_PRODUCT_LIST',
        payload: getProducts.products,
      });
      setTotalItems(getProducts.numberOfProducts);
    }
  }, [getProducts, totalItems]);

  // Lagre valgte produkter separat
  const onSubmit = (e: any) => {
    e.preventDefault();
    const selectedProductIds: string[] = [
      ...state.productInvestmentList.map((product) => product.id),
      ...state.productAgreementList.map((product) => product.id),
    ];

    console.log(selectedProductIds);

    postSaleProducts.mutate(selectedProductIds, {
      onSuccess: (res: any) => {
        console.log('onSubmit response: ', res);
        dispatch({
          type: 'CLEAR_PRODUCTS',
        });
      },
    });

    setDrawerOpen(false);
  };

  // Function to handle pagination
  const handlePageChange = (event: any, value: number) => {
    setPage(value);
  };
  const handleChangeNumberPerPage = (e: any) => {
    e.preventDefault();
    const value = e.target.value;
    setItemsPerPage(value);
  };
  const handleSelectProduct = (e: any, product: IProductViewModel) => {
    e.preventDefault();

    if (ticketProductVariant === EnumProductVariant.Investment) {
      dispatch({
        type: 'ADD_INVESTMENT_PRODUCT',
        payload: product,
      });
    } else if (ticketProductVariant === EnumProductVariant.Agreement) {
      dispatch({
        type: 'ADD_AGREEMENT_PRODUCT',
        payload: product,
      });
    } else if (ticketProductVariant === EnumProductVariant.Consumable) {
      dispatch({
        type: 'ADD_CONSUMABLE_PRODUCT',
        payload: product,
      });
    }
  };

  const handleRemoveProduct = (
    event: any,
    product: IProductViewModel,
    index: number
  ) => {
    event.preventDefault();
    if (ticketProductVariant === EnumProductVariant.Investment) {
      dispatch({
        type: 'REMOVE_INVESTMENT_PRODUCT',
        payload: { product, index },
      });
    } else if (ticketProductVariant === EnumProductVariant.Agreement) {
      dispatch({
        type: 'REMOVE_AGREEMENT_PRODUCT',
        payload: { product, index },
      });
    } else if (ticketProductVariant === EnumProductVariant.Consumable) {
      dispatch({
        type: 'REMOVE_CONSUMABLE_PRODUCT',
        payload: { product, index },
      });
    }
  };

  // Paginate the products
  const paginateProducts = (products: any[]) => {
    return products; //.slice(startIndex, startIndex + itemsPerPage);
  };

  const clearSelection = () => {
    dispatch({
      type: 'CLEAR_PRODUCTS',
    });
    console.log('Selection cleared.');
  };

  // Render the product list with selection toggle
  const renderProductList = (products: IProductViewModel[]) => {
    console.log('getProducts: renderProductList:', products); // Logg produktene som vises
    return (
      <>
        <List
          sx={{
            width: '100%',
            p: 2,
            height: height - 230,
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          {paginateProducts(products).map(
            (product: IProductViewModel, index: number) => (
              <Paper
                key={index}
                sx={{
                  margin: 1,
                  backgroundColor: state.productCatalogueList.some(
                    (p) => p.id === product.id
                  )
                    ? 'primary.main'
                    : 'primary.main',
                  ':hover': {
                    cursor: 'pointer',
                    filter: 'brightness(120%)',
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
                onClick={(e) => {
                  handleSelectProduct(e, product);
                }}
              >
                {/* Conditionally render based on the product type */}
                {objectType === EnumObjectType.Product && (
                  <SingleEmployeeDrawerProductView
                    product={product as IProductViewModel}
                    isOnboarding={isOnboarding}
                  />
                )}
              </Paper>
            )
          )}
        </List>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
          <Pagination
            count={Math.ceil(totalItems / itemsPerPage)}
            page={page}
            onChange={handlePageChange}
          />
          <Typography sx={{ mr: 2 }}>Items per page:</Typography>
          <Select
            size='small'
            variant='outlined'
            value={itemsPerPage}
            onChange={(e) => handleChangeNumberPerPage(e)}
            sx={{ width: 85, mt: -1 }}
          >
            {[15, 25, 50, 100].map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </>
    );
  };

  // Render the selected products inside the drawer on the right side
  const renderSelectedProductList = () => {
    return (
      <Box sx={{ width: '100%', height: height, p: 2 }}>
        {/* Selected Company Products */}
        <Typography variant='subtitle1' sx={{ letterSpacing: 4 }}>
          {ticketProductVariant === EnumProductVariant.Consumable
            ? t('ticketProductDrawer.consumableProduct').toUpperCase()
            : ticketProductVariant === EnumProductVariant.Agreement
            ? t('ticketProductDrawer.agreementProduct').toUpperCase()
            : t('ticketProductDrawer.investmentProduct').toUpperCase()}
        </Typography>
        <List
          sx={{
            width: '100%',
            m: 0,
            p: 1,
            backgroundColor: 'primary.back',
            height: height - 218,
            overflow: 'auto',
          }}
        >
          {ticketProductVariant === EnumProductVariant.Investment &&
            state.productInvestmentList.map(
              (product: IProductViewModel, index: number) => (
                <SingleProductView key={index} product={product} />
              )
            )}
        </List>
      </Box>
    );
  };

  const renderSelectedProductInfo = () => {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        {/* Selected Company Products */}
        <Typography variant='subtitle1' sx={{ height: 30 }}></Typography>
        <List
          sx={{
            width: '100%',
            m: 0,
            p: 1,
            backgroundColor: 'transparent',
            height: height / 2 - 120,

            overflow: 'auto',
          }}
        >
          <Typography variant='subtitle2' sx={{ letterSpacing: 2 }}>
            {ticketProductVariant === EnumProductVariant.Consumable
              ? t('ticketProductDrawer.consumableProductInfo').toLowerCase()
              : ticketProductVariant === EnumProductVariant.Agreement
              ? t('ticketProductDrawer.agreementProductInfo').toLowerCase()
              : t('ticketProductDrawer.investmentProductInfo').toLowerCase()}
          </Typography>
        </List>
      </Box>
    );
  };

  const filteredProducts = state.productCatalogueList.filter(
    (product: IProductViewModel) => {
      // Apply company filter
      if (
        product.thirdParty !== companyFilter &&
        companyFilter !== ThirdPartEnum.None
      ) {
        return false;
      }

      // Apply search filter
      if (!searchQuery) {
        return true;
      }

      const searchableFields: (keyof IProductViewModel)[] = ['name', 'comment'];

      return searchableFields.some((field) => {
        const fieldValue = product[field as keyof IProductViewModel];
        return (
          fieldValue &&
          fieldValue
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        );
      });
    }
  );

  console.log('getProducts: state:', state); // Logg produktene som vises

  return (
    <>
      <Box
        sx={[
          {
            width: 32,
            height: 32,
          },
        ]}
      >
        <IconButton
          sx={{
            p: 0,
            m: 0,
            width: 18,
            height: 18,
          }}
          onClick={() => setDrawerOpen(true)}
        >
          <AddRoundedIcon sx={{ fontSize: 26 }} />
        </IconButton>
      </Box>

      <Drawer
        anchor='left'
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{ width: '100%', flexShrink: 0 }}
        PaperProps={{ style: { width: '100%' } }}
      >
        <Box sx={{ display: 'flex', padding: 2 }}>
          {/* Left side: Product list */}
          <Box sx={{ width: '40%' }}>
            {/* Render category buttons */}
            <Grid
              container
              sx={{ display: 'flex', flexDirection: 'row' }}
            ></Grid>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography variant='h6'>
                {t('ticketProductDrawer.listHeader')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Company filter dropdown */}
                <Select
                  value={companyFilter}
                  onChange={(e) =>
                    setCompanyFilter(e.target.value as ThirdPartEnum)
                  }
                  size='small'
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value={ThirdPartEnum.None}>All Vendors</MenuItem>
                  <MenuItem value={ThirdPartEnum.CloudFactory}>
                    CloudFactory
                  </MenuItem>
                  <MenuItem value={ThirdPartEnum.ExLedger}>ExLedger</MenuItem>
                </Select>

                {/* Search field */}
                <TextField
                  variant='outlined'
                  size='small'
                  placeholder='Search products'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: 200 }}
                />
              </Box>
            </Box>

            {renderProductList(filteredProducts)}
          </Box>

          {/* Right side: Selected products */}

          <Stack
            sx={{
              width: '40%',
              display: 'flex',
              flexDirection: 'column',
              height: height - 28,
            }}
          >
            {renderSelectedProductList()}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pt: 2,
                pl: 3,
                pr: 3,
                mt: -4,
              }}
            >
              <Button
                type='submit'
                variant='contained'
                onClick={clearSelection}
              >
                Clear
              </Button>
              <Button type='submit' variant='contained' onClick={onSubmit}>
                Save
              </Button>
            </Box>
          </Stack>
          {/* Right side: Selected products */}
          <Stack
            sx={{
              width: '20%',
              display: 'flex',
              flexDirection: 'column',
              height: height - 28,
            }}
          >
            <Stack
              display='flex'
              direction='row'
              sx={{ m: 0, justifyContent: 'flex-end' }}
            >
              <Button
                type='submit'
                variant='contained'
                onClick={() => setDrawerOpen(false)}
              >
                X
              </Button>
            </Stack>
            <Box sx={{ width: '100%' }}>{renderSelectedProductInfo()}</Box>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
};
